import { listFeasibilityStudiesQueryOptions } from "@packages/api-client/hooks/feasibilityController";
import type {
	FeasibilityStudyStatus,
	FeasibilityStudyType,
} from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
	AlertCircle,
	Building2,
	CheckCircle,
	Clock,
	FileText,
	Plus,
	Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteStudy } from "@/api/feasibility/studies";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEstablishment } from "@/hooks/useEstablishment";
import { isAllFarms, useFarmScope } from "@/hooks/useFarmScope";
import { cn } from "@/utils/utils";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/feasibility/")(
	{
		component: FeasibilityListPage,
	},
);

function FeasibilityListPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/feasibility/",
	});
	const scope = useFarmScope();
	const { establishments } = useEstablishment();
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [studyToDelete, setStudyToDelete] = useState<string | null>(null);

	const { data, isLoading } = useQuery(
		listFeasibilityStudiesQueryOptions(orgId, {
			establishmentId:
				scope.kind === "single" ? scope.establishmentId : undefined,
		}),
	);
	const studies = data?.studies ?? [];
	const deleteStudy = useDeleteStudy();

	// Map farm id → name to label which farm each study belongs to under All Farms
	// (the list response carries `establishmentId` but not the farm name).
	const farmNameMap = useMemo(() => {
		const map: Record<string, string> = {};
		for (const farm of establishments) {
			map[farm.id] = farm.name;
		}
		return map;
	}, [establishments]);

	const getStatusIcon = (status: FeasibilityStudyStatus) => {
		switch (status) {
			case "DRAFT":
				return <FileText className="h-4 w-4" />;
			case "PENDING_CALC":
				return <Clock className="h-4 w-4 animate-spin" />;
			case "CALCULATED":
				return <CheckCircle className="h-4 w-4" />;
			case "ERROR":
				return <AlertCircle className="h-4 w-4" />;
			default:
				return null;
		}
	};

	const getStatusColor = (status: FeasibilityStudyStatus) => {
		switch (status) {
			case "DRAFT":
				return "bg-base-300 text-base-content";
			case "PENDING_CALC":
				return "bg-info/20 text-info";
			case "CALCULATED":
				return "bg-success/20 text-success";
			case "ERROR":
				return "bg-error/20 text-error";
			default:
				return "";
		}
	};

	const getTypeLabel = (type: FeasibilityStudyType) => {
		return t(`feasibility.types.${type}`);
	};

	const handleDeleteClick = (studyId: string) => {
		setStudyToDelete(studyId);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (studyToDelete) {
			await deleteStudy.mutateAsync({
				organizationId: orgId,
				studyId: studyToDelete,
			});
		}
		setDeleteDialogOpen(false);
		setStudyToDelete(null);
	};

	return (
		<div className="h-full flex flex-col space-y-3">
			<PageHeader
				title={t("feasibility.list.title")}
				description={t("feasibility.list.createFirst")}
				showBackButton={false}
			/>

			<Card className="flex-1 flex flex-col overflow-hidden">
				<CardHeader className="pb-3 flex flex-row items-center justify-between">
					<CardTitle className="text-lg">
						{t("feasibility.list.title")}
					</CardTitle>
					<Link to="/app/org/$orgId/feasibility/new" params={{ orgId }}>
						<Button>
							<Plus className="h-4 w-4 mr-2" />
							{t("feasibility.list.newStudy")}
						</Button>
					</Link>
				</CardHeader>

				<CardContent className="flex-1 overflow-auto">
					{isLoading ? (
						<div className="flex items-center justify-center h-40">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
						</div>
					) : !studies || studies.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-40 text-center">
							<FileText className="h-12 w-12 text-base-content/30 mb-4" />
							<p className="text-base-content/70">
								{t("feasibility.list.noStudies")}
							</p>
							<p className="text-sm text-base-content/40 mt-1">
								{t("feasibility.list.createFirst")}
							</p>
						</div>
					) : (
						<div className="space-y-3">
							{studies.map((study) => (
								<Link
									key={study.id}
									to="/app/org/$orgId/feasibility/$studyId"
									params={{ orgId, studyId: study.id }}
									className="block"
								>
									<Card className="hover:border-primary transition-colors cursor-pointer">
										<CardContent className="p-4">
											<div className="flex items-center justify-between">
												<div className="flex-1 min-w-0">
													<h3 className="font-medium truncate">{study.name}</h3>
													<p className="text-sm text-base-content/70 truncate">
														{getTypeLabel(study.type as FeasibilityStudyType)}
													</p>
													{isAllFarms(scope) && (
														<p className="mt-0.5 flex items-center gap-1 text-xs text-base-content/50 truncate">
															<Building2 className="h-3 w-3 shrink-0" />
															{farmNameMap[study.establishmentId] ?? "-"}
														</p>
													)}
												</div>
												<div className="flex items-center gap-3">
													<Badge
														className={cn(
															"flex items-center gap-1",
															getStatusColor(
																study.status as FeasibilityStudyStatus,
															),
														)}
													>
														{getStatusIcon(
															study.status as FeasibilityStudyStatus,
														)}
														{t(`feasibility.status.${study.status}`)}
													</Badge>
													<Button
														variant="ghost"
														size="icon"
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															handleDeleteClick(study.id);
														}}
													>
														<Trash2 className="h-4 w-4 text-error" />
													</Button>
												</div>
											</div>
										</CardContent>
									</Card>
								</Link>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("common.confirmDelete")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("common.deleteConfirmDescription")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmDelete}
							className="bg-error text-error-content hover:bg-error/90"
						>
							{t("common.delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
